import naclUtil from 'tweetnacl-util';

import { TIME_CLASSES } from '../chess/game.js';

export const CERT_SHARD_PREFIX = 'certs';
export const CERT_SHARD_COUNT = 256;
/** Concurrent shard rooms an archivist keeps hot while rotating through all 256. */
export const ARCHIVIST_ACTIVE_WINDOW = 32;
export const ARCHIVIST_ROTATE_MS = 8_000;
/** @deprecated Prefer allCertificateShards(); kept for older tests/settings. */
export const ARCHIVIST_SHARD_LIMIT = CERT_SHARD_COUNT;
export const LOBBY_GOSSIP_SHARD_LIMIT = 24;

export function certificateShard(publicKey) {
  const signingPublicKey = typeof publicKey === 'string'
    ? publicKey
    : publicKey?.signingPublicKey;
  if (!signingPublicKey) {
    throw new Error('A signing public key is required for certificate sharding');
  }
  const bytes = naclUtil.decodeBase64(signingPublicKey);
  return `${CERT_SHARD_PREFIX}:${bytes[0].toString(16).padStart(2, '0')}`;
}

export function certificateShards(certificate) {
  return [...new Set(certificate.players.map((player) =>
    certificateShard(player.publicKey)
  ))].sort();
}

/** Every certificate shard (`certs:00` … `certs:ff`). */
export function allCertificateShards() {
  return Array.from({ length: CERT_SHARD_COUNT }, (_, index) =>
    `${CERT_SHARD_PREFIX}:${index.toString(16).padStart(2, '0')}`
  );
}

export function normalizeArchivistShards(shards) {
  return [...new Set(shards)]
    .filter((scope) => /^certs:[a-f0-9]{2}$/.test(scope))
    .sort()
    .slice(0, ARCHIVIST_SHARD_LIMIT);
}

/** Sliding window so an archivist eventually covers every shard without 256 hot rooms. */
export function archivistWindowShards(
  allShards = allCertificateShards(),
  offset = 0,
  windowSize = ARCHIVIST_ACTIVE_WINDOW
) {
  if (!allShards.length) {
    return [];
  }
  const size = Math.min(Math.max(1, windowSize), allShards.length);
  const start = ((offset % allShards.length) + allShards.length) % allShards.length;
  const out = [];
  for (let index = 0; index < size; index += 1) {
    out.push(allShards[(start + index) % allShards.length]);
  }
  return out;
}

/** Own shard plus peer home shards discovered in the lobby (bounded). */
export function collectLobbyGossipShards(ownShard, peerShards = [], limit = LOBBY_GOSSIP_SHARD_LIMIT) {
  return [...new Set([ownShard, ...peerShards])]
    .filter((scope) => /^certs:[a-f0-9]{2}$/.test(scope))
    .sort()
    .slice(0, Math.max(1, limit));
}

export function leaderboardScope({
  mode = 'standard',
  timeClass,
  variant = 'standard',
  period,
  shard
}) {
  if (!['standard', 'variant'].includes(mode)) {
    throw new Error(`Unsupported leaderboard mode: ${mode}`);
  }
  if (!TIME_CLASSES.includes(timeClass)) {
    throw new Error(`Unsupported leaderboard class: ${timeClass}`);
  }
  if (mode === 'variant' && !variant) {
    throw new Error('A variant is required for variant leaderboards');
  }
  if (!['weekly', 'all-time'].includes(period)) {
    throw new Error(`Unsupported leaderboard period: ${period}`);
  }
  if (!/^certs:[a-f0-9]{2}$/.test(shard)) {
    throw new Error(`Invalid certificate shard: ${shard}`);
  }
  const shardSuffix = shard.slice(-2);
  if (mode === 'variant') {
    return `leaderboard:variant:${variant}:${timeClass}:${period}:${shardSuffix}`;
  }
  return `leaderboard:standard:${timeClass}:${period}:${shardSuffix}`;
}
